import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BlankModal, HStack, ModalBody, ModalFooter, ModalHeader, ModalProps } from '@cad-arc';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { useFullscreenModalControls } from '../../modals/context/modal-context-provider';
import { useRequestAssistanceApi } from '../hooks/use-request-assistance-api';
import { RequestAssistanceForm } from './request-assistance-form';
import { FormCancelButton } from '../../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';
import { IntialAssistanceAgencyType, RequestAssistanceFormDisplayType } from '../types';
import { ConstrainedModalProps } from '../../modals/types';
import { useOpenEventPanel } from '../../event-panel/use-open-event-panel';
import { RequestAssistanceResponse } from '@mark43/cad-api';

export type RequestAssistanceModalComponentType = typeof RequestAssistanceModal;

export type RequestAssistanceModalProps = {
    agencyEventId: number;
    initialAssistanceAgencyTypes?: IntialAssistanceAgencyType[];
    portalProps?: ModalProps['portalProps'];
} & ConstrainedModalProps;

export const RequestAssistanceModal: React.FC<RequestAssistanceModalProps> = ({
    agencyEventId,
    initialAssistanceAgencyTypes,
    portalProps,
    constrainToParentPortal,
    closeOnOverlayClick,
    onClose,
}) => {
    const modalControls = useFullscreenModalControls();
    const openEventPanel = useOpenEventPanel();
    const finalOnClose = onClose ?? modalControls.close;
    const onCancel = React.useCallback(
        function onCancel(): void {
            finalOnClose();
        },
        [finalOnClose]
    );

    const onSuccess = React.useCallback(
        function onSuccess(resp: RequestAssistanceResponse): void {
            finalOnClose();

            resp.agencyEventIds.forEach((agencyEventId) => {
                openEventPanel({
                    eventId: agencyEventId,
                });
            });
        },
        [finalOnClose, openEventPanel]
    );

    const {
        requestAssistanceFormData,
        requestAssistance,
        query,
        mutation,
    } = useRequestAssistanceApi(agencyEventId, onSuccess);

    const form = useForm<RequestAssistanceFormDisplayType>({
        mode: 'onBlur',
        defaultValues: { forms: [] },
    });

    const assistanceForms = form.watch('forms');
    function onSubmit(formShape: RequestAssistanceFormDisplayType): void {
        requestAssistance(formShape);
    }

    return (
        <IntegratedFormProvider
            query={query}
            mutation={mutation}
            form={form}
            onSubmit={onSubmit}
            inputSize="sm"
        >
            <BlankModal
                portalProps={portalProps}
                size="md"
                scrollBehavior="inside"
                constrainToPortal={constrainToParentPortal}
                onClose={onCancel}
                isOpen={true}
                style={{ maxHeight: 535 }}
                className="request-assistance-modal"
                closeOnOverlayClick={closeOnOverlayClick}
            >
                <ModalHeader>Request Assistance</ModalHeader>
                <ModalBody>
                    <FormBody>
                        <FormProvider {...form}>
                            {requestAssistanceFormData && (
                                <RequestAssistanceForm
                                    formData={requestAssistanceFormData}
                                    initialAssistanceAgencyTypes={initialAssistanceAgencyTypes}
                                />
                            )}
                        </FormProvider>
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <HStack>
                        <FormCancelButton onClick={onCancel} />
                        <FormSaveButton disabled={!assistanceForms.length}>
                            Send Request
                        </FormSaveButton>
                    </HStack>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
};
